import { HubConnectionState } from "@microsoft/signalr";
import { loadWrapper } from "aic";
import { useUnsavedChanges } from "contexts/UnsavedChangesContextProvider";
import { MosaicTocEntryDto, REFRESH, UPDATE_ENTRY } from "contexts/toc/interface";
import { eAccessRights, eHierarchyLevel, eReleaseStatus, eTlfItemType } from "functions/api/enums";
import { isTlfItemReadDto, TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { hydrateArsTableBody } from "functions/api/tlfItem/hydrateArsTableBody";
import { patchTlfItem } from "functions/api/tlfItem/patchTlfItem";
import { saveTlfItem } from "functions/api/tlfItem/saveTlfItem";
import { toIndexDto } from "functions/toIndexDto";
import { useCallback, useContext } from "react";
import { createPatch } from "rfc6902";
import { AppSettingsContext, useTocContext } from "../AppSettingsContext";
import { SignalRContext } from "../SignalRProvider";
import { SAVE_HIERARCHY, UPDATE_TLF_ITEM_INDEX } from "../hierarchy/interface";
import { getTlfUrl } from "./getTlfUrl";
import {
  ADD_RECEIVED_MESSAGE,
  ITlfActionAddReceivedMessage,
  ITlfContext,
  SET_TLF_REQUEST_STATUS,
  TlfRequestStatusType,
  UPDATE_TLF,
} from "./interface";

/**
 * Provides the complete TLF context API from unified AppSettings state.
 * All TLF data, request statuses, and command dependencies are read from the unified context.
 *
 * @returns The unified TLF state, command callbacks, and derived read-only flag.
 */
export const useTlfContext = (): ITlfContext => {
  const { state, dispatch } = useContext(AppSettingsContext);
  const signalR = useContext(SignalRContext);
  const { navigateAfterSave } = useUnsavedChanges("tlf-context");
  const hierarchyState = state.hierarchyState;
  const { state: tocState } = useTocContext();
  const tlfState = state.tlfState;

  const setRequestStatus = useCallback(
    (status: TlfRequestStatusType) =>
      dispatch({
        operation: SET_TLF_REQUEST_STATUS,
        requestStatus: status,
        calledFrom: "useTlfContext:SetSaveRequestStatus",
      }),
    [dispatch],
  );

  const sendSignalRPatch = useCallback(
    async (newTlfData: TlfItemReadDto) => {
      // istanbul ignore next
      if (!tlfState.tlfData || signalR?.connection?.state !== HubConnectionState.Connected) return;
      // Save and patch responses invoke this callback only while a TLF item is loaded.
      const signalRGroup = `tlf${tlfState.tlfData.id}`;
      // The callback is only used for connected SignalR updates.
      /* istanbul ignore else */
      const lastSave = tlfState.tlfDataVersions[tlfState.ixSaveVersion];
      if (lastSave) {
        const allowedKeys = Object.keys(lastSave).filter(
          (key) => !["saved", "changed", "inError", "history"].includes(key),
        );
        const signalRUpdate = {
          updatedItemId: lastSave.id,
          patch: createPatch(
            Object.fromEntries(
              allowedKeys.map((key) => [key, lastSave[key as keyof TlfItemReadDto]]),
            ),
            Object.fromEntries(
              allowedKeys.map((key) => [key, newTlfData[key as keyof TlfItemReadDto]]),
            ),
          ),
        };
        const sentMessage = await signalR.sendTlfUpdateToGroup(signalRUpdate, signalRGroup);
        const receivedMessageAction: ITlfActionAddReceivedMessage = {
          operation: ADD_RECEIVED_MESSAGE,
          calledFrom: "useTlfContext:SendSignalRPatch",
          receivedMessage: sentMessage,
        };
        dispatch(receivedMessageAction);
      }
    },
    [dispatch, signalR, tlfState.ixSaveVersion, tlfState.tlfData, tlfState.tlfDataVersions],
  );

  const saveTlf = useCallback(
    async (
      azsolId?: string,
      version?: number,
      domain?: string,
      primaryHierarchyId?: string,
      additionalInformation?: string,
      copiedFromHierarchyLevel?: eHierarchyLevel,
      tocNumber?: string,
      sapReference?: string,
    ) => {
      // Save commands are issued only from a loaded TLF route with a hierarchy context.
      /* istanbul ignore else */
      if (hierarchyState.hierarchy && tlfState.tlfData && isTlfItemReadDto(tlfState.tlfData)) {
        const currentTlfData = tlfState.tlfData;
        const response = await loadWrapper(
          saveTlfItem,
          {
            tlfItem: {
              ...currentTlfData,
              tocNumber: tocNumber ?? currentTlfData.tocNumber,
              sapReference: sapReference ?? currentTlfData.sapReference,
            },
            newDetails:
              azsolId &&
              version !== undefined &&
              additionalInformation !== undefined &&
              primaryHierarchyId
                ? {
                    azsolId,
                    version,
                    domain: domain ?? "",
                    additionalInformation,
                    primaryHierarchyId,
                  }
                : undefined,
            copiedFromHierarchyLevel,
          },
          tlfState.requestStatus,
          setRequestStatus,
          state.backEndUrl,
        );
        if (response?.success) {
          // Cross-hierarchy copies are hydrated by the route loader using the refreshed target ARS.
          if (
            (response.data.type === eTlfItemType.arsTable ||
              response.data.type === eTlfItemType.arsListing) &&
            response.data.primaryHierarchyId === hierarchyState.hierarchy.id
          )
            hydrateArsTableBody(hierarchyState.hierarchyNodes, response.data);
          const newTlfItemIndex = toIndexDto(response.data);
          const newTlfIndex = [
            ...(hierarchyState.tlfItemIndex ?? []).filter(
              (item) =>
                !(item.azsolId === response.data.azsolId && item.version === response.data.version),
            ),
            newTlfItemIndex,
          ].sort((first, second) => first.azsolId.localeCompare(second.azsolId));
          dispatch({ operation: UPDATE_TLF_ITEM_INDEX, tlfItemIndex: newTlfIndex });
          if (hierarchyState.changed) dispatch({ operation: SAVE_HIERARCHY, noRedirect: true });
          const newTocItem: MosaicTocEntryDto = {
            ...response.data,
            changed: false,
            inError: false,
            changedTitles: response.data.changedTitles ?? [],
          };
          const newTocItems = [
            ...tocState.tocItems.filter((item) => !(item.id === newTocItem.id)),
            newTocItem,
          ];
          dispatch({
            operation: REFRESH,
            hierarchyId: hierarchyState.hierarchy.id,
            items: newTocItems,
          });
          dispatch({
            operation: UPDATE_TLF,
            tlfData: response.data,
            newSavePoint: true,
            calledFrom: "useTlfContext:SaveResponse",
          });
          await sendSignalRPatch(response.data);
          const targetHierarchy = hierarchyState.hierarchyNodes.find(
            (item) => item.id === response.data.primaryHierarchyId,
          );
          // The route loader supplies the target hierarchy for a successful save.
          const newUrl = targetHierarchy
            ? getTlfUrl(targetHierarchy, response.data)
            : `/TlfDesigner/${response.data.azsolId}/${response.data.version}`;
          // Save navigation is only needed when the saved route differs from the current route.
          /* istanbul ignore else */
          if (newUrl !== window.location.pathname) navigateAfterSave(newUrl);
        }
      }
    },
    [
      dispatch,
      hierarchyState.changed,
      hierarchyState.hierarchy,
      hierarchyState.hierarchyNodes,
      hierarchyState.tlfItemIndex,
      navigateAfterSave,
      sendSignalRPatch,
      setRequestStatus,
      state.backEndUrl,
      tlfState.requestStatus,
      tlfState.tlfData,
      tocState.tocItems,
    ],
  );

  const patchTlf = useCallback(
    async <K extends keyof TlfItemReadDto>(
      id: string,
      azsolId: string,
      key: K,
      newValue: TlfItemReadDto[K],
    ) => {
      const response = await loadWrapper(
        patchTlfItem,
        {
          id: id,
          azsolId: azsolId,
          key,
          newValue,
        },
        tlfState.requestStatus,
        setRequestStatus,
        state.backEndUrl,
      );
      // The API wrapper reports successful responses for the current TLF item.
      /* istanbul ignore else */
      if (response?.success) {
        // Update TLF item
        // Successful patches are returned for the loaded TLF item.
        /* istanbul ignore else */
        if (tlfState.tlfData && response.data.id === tlfState.tlfData.id) {
          dispatch({
            operation: UPDATE_TLF,
            calledFrom: "useTlfContext:PatchResponse",
            tlfData: response.data,
            newSavePoint: true,
          });
        }
        // Update tocContext
        // TODO: Should be updated by UPDATE_TLF
        dispatch({
          operation: UPDATE_ENTRY,
          update: response.data,
        });
        await sendSignalRPatch(response.data);
      }
    },
    [
      dispatch,
      sendSignalRPatch,
      setRequestStatus,
      state.backEndUrl,
      tlfState.requestStatus,
      tlfState.tlfData,
    ],
  );

  const readOnly =
    tlfState.readOnly ||
    hierarchyState.hierarchy?.isBackupVersion === true ||
    !hierarchyState.accessRights.includes(eAccessRights.WriteToTlfItemsInHierarchy) ||
    (tlfState.tlfData !== null &&
      ![eReleaseStatus.Draft, eReleaseStatus.ReleasePending].includes(
        tlfState.tlfData.releaseStatus,
      ));

  return {
    ...tlfState,
    dispatch,
    patchTlf,
    readOnly,
    saveTlf,
  };
};

useTlfContext.displayName = "useTlfContext";
